import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { FiX } from 'react-icons/fi';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { useBalance } from '../context/BalanceContext';
import { usePrices } from '../context/PriceContext';
import CoinSelectorControl from '../components/ui/CoinSelectorControl';

const SUPPORTED_COINS = [
  { value: 'USDT', label: 'Tether (USDT)' },
  { value: 'USDC', label: 'USD Coin (USDC)' },
  { value: 'BTC', label: 'Bitcoin (BTC)' },
  { value: 'ETH', label: 'Ethereum (ETH)' },
  { value: 'SOL', label: 'Solana (SOL)' },
  { value: 'XRP', label: 'Ripple (XRP)' }
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const PLAN_SUBSCRIPTION_LIMITS = {
  'plan-a':  5,
  'plan-b':  5,
  'plan-c':  5,
  'plan-d':  3,
  'plan-e':  2,
  'vip-plan': 1,
};

const formatDurationLabel = (ms) => {
  if (!ms) return 'Flexible term';
  const days = Math.round(ms / MS_PER_DAY);
  if (days >= 30 && days % 30 === 0) {
    const months = days / 30;
    return `${months} month${months > 1 ? 's' : ''}`;
  }
  if (days > 1) return `${days} days`;
  return `${days} day`;
};

const ARBITRAGE_PLANS = [
    {
        id: 'plan-a',
        name: 'Plan A  1 Day',
        durationDays: 1,
        minAmount: 200,
        maxAmount: 2000,
        dailyReturnRange: { min: 0.032, max: 0.05 },
        cancelPenaltyRate: 0.05,
        description: 'Entry-level allocation capturing intraday spreads with same-day settlement.',
        gradient: 'from-sky-500 via-cyan-500 to-emerald-500',
        riskLevel: 'Short  Hedged',
        signalTag: 'Daily gap capture',
    },
    {
        id: 'plan-b',
        name: 'Plan B  3 Days',
        durationDays: 3,
        minAmount: 2000,
        maxAmount: 5000,
        dailyReturnRange: { min: 0.05, max: 0.07 },
        cancelPenaltyRate: 0.08,
        description: 'Multi-day neutral rotation optimised for medium ticket sizes.',
        gradient: 'from-teal-500 via-blue-500 to-indigo-500',
        riskLevel: 'Short-term  Managed',
        signalTag: 'Spread rebalancer',
    },
    {
        id: 'plan-c',
        name: 'Plan C  5 Days',
        durationDays: 5,
        minAmount: 5000,
        maxAmount: 15000,
        dailyReturnRange: { min: 0.07, max: 0.087 },
        cancelPenaltyRate: 0.10,
        description: 'Five-day corridor targeting mid-volatility cross-exchange pricing.',
        gradient: 'from-emerald-500 via-teal-500 to-blue-500',
        riskLevel: 'Short  Managed',
        signalTag: 'Volatility neutraliser',
    },
    {
        id: 'plan-d',
        name: 'Plan D  7 Days',
        durationDays: 7,
        minAmount: 15000,
        maxAmount: 75000,
        dailyReturnRange: { min: 0.087, max: 0.1 },
        cancelPenaltyRate: 0.12,
        description: 'Weekly rotation with rolling hedges for higher notional deployments.',
        gradient: 'from-amber-500 via-orange-500 to-rose-500',
        riskLevel: 'Balanced  Managed',
        signalTag: 'Weekly hedge stack',
    },
    {
        id: 'plan-e',
        name: 'Plan E  15 Days',
        durationDays: 15,
        minAmount: 75000,
        maxAmount: 300000,
        dailyReturnRange: { min: 0.1, max: 0.15 },
        cancelPenaltyRate: 0.15,
        description: 'Fortnightly spread programme balancing liquidity depth with adaptive routing.',
        gradient: 'from-rose-500 via-fuchsia-500 to-purple-500',
        riskLevel: 'Core  Managed',
        signalTag: 'Dual venue hedge',
    },
    {
        id: 'vip-plan',
        name: 'VIP Plan  30 Days',
        durationDays: 30,
        minAmount: 300000,
        maxAmount: 1000000,
        dailyReturnRange: { min: 0.15, max: 0.45 },
        cancelPenaltyRate: 0.20,
        description: 'Institutional grade cycle with month-long managed execution and escalated spreads.',
        gradient: 'from-violet-500 via-indigo-500 to-purple-500',
        riskLevel: 'Extended  Managed',
        signalTag: 'Macro-aware balancing',
    }
].map(p => ({ ...p, durationLabel: formatDurationLabel(p.durationDays * MS_PER_DAY) }));


const formatCurrency = (amount, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(amount || 0));
  } catch (error) {
    return `${Number(amount || 0).toFixed(2)} ${currency}`;
  }
};

const formatCoinAmount = (amount, coinSymbol = '') => {
  const value = Number(amount || 0);
  const symbol = coinSymbol?.toUpperCase?.() || '';
  return `${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8
  })}${symbol ? ` ${symbol}` : ''}`;
};

const formatCoinWithUsd = (amount, coinSymbol, prices) => {
  const primary = formatCoinAmount(amount, coinSymbol);
  const price = prices?.[coinSymbol?.toUpperCase?.() || ''];
  if (!price) {
    return { primary, secondary: null };
  }
  const usdValue = Number(amount || 0) * Number(price || 0);
  return {
    primary,
    secondary: formatCurrency(usdValue)
  };
};

const formatCoinRangeWithUsd = (minAmount, maxAmount, coinSymbol, prices) => {
  // Convert USDT amounts to coin equivalent
  const coinPrice = prices?.[coinSymbol?.toUpperCase?.()] || 0;
  if (coinPrice === 0 || coinSymbol === 'USDT' || coinSymbol === 'USDC') {
    // For stablecoins or when price unavailable, use direct amounts
    const min = formatCoinWithUsd(minAmount, coinSymbol, prices);
    const max = formatCoinWithUsd(maxAmount, coinSymbol, prices);
    const label = (entry) => (entry.secondary ? `${entry.primary} (${entry.secondary})` : entry.primary);
    return `${label(min)}  ${label(max)}`;
  }
  
  // For other coins, convert USDT amounts to coin amounts
  const minCoinAmount = minAmount / coinPrice;
  const maxCoinAmount = maxAmount / coinPrice;
  const min = formatCoinWithUsd(minCoinAmount, coinSymbol, prices);
  const max = formatCoinWithUsd(maxCoinAmount, coinSymbol, prices);
  const label = (entry) => (entry.secondary ? `${entry.primary} (${entry.secondary})` : entry.primary);
  return `${label(min)}  ${label(max)}`;
};

const formatPercent = (value, digits = 1) => `${(Number(value || 0) * 100).toFixed(digits)}%`;

const getPlanRange = (plan) => {
  if (!plan) {
    return {
      days: 0,
      minDaily: 0,
      maxDaily: 0,
      minTerm: 0,
      maxTerm: 0,
      minApr: 0,
      maxApr: 0
    };
  }

  const days = plan.durationDays ?? 0;
  const minDaily = plan.dailyReturnRange?.min ?? 0;
  const maxDaily = plan.dailyReturnRange?.max ?? 0;
  const minTerm = minDaily * days;
  const maxTerm = maxDaily * days;
  return {
    days,
    minDaily,
    maxDaily,
    minTerm,
    maxTerm,
    minApr: minDaily * 365,
    maxApr: maxDaily * 365
  };
};

const computeProfitRange = (plan, principal = 0) => {
  const range = getPlanRange(plan);
  const amount = Number(principal || 0);
  const min = amount * range.minTerm;
  const max = amount * range.maxTerm;
  const avg = (min + max) / 2;
  return { min, max, avg };
};

const getTradeStatus = (trade, now) => {
  // Normalize to lowercase so admin can type any casing in Firebase Console
  const stored = String(trade?.status || '').toLowerCase().trim();
  // Terminal states
  if (stored === 'cancelled' || stored === 'canceled') return 'cancelled';
  if (stored === 'rejected') return 'rejected';
  if (stored === 'claimed') return 'claimed';
  if (stored === 'claiming') return 'claiming';
  // Pending - waiting for admin approval
  if (stored === 'pending' || stored === '') return 'pending';
  // Active - admin approved, funds locked
  if (stored === 'active') {
    if (!trade?.startTime || !trade?.durationMs) return 'active';
    if (now >= trade.startTime + trade.durationMs) return 'completed';
    return 'active';
  }
  // Completed - matured and ready to claim
  if (stored === 'completed') return 'completed';
  // Default
  return stored || 'pending';
};


const ArbitrageTrading = () => {
  const { currentUser, userData } = useAuth();
  const { getBalance } = useBalance();
  const { prices, priceChanges } = usePrices();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [amount, setAmount] = useState('');
  const [selectedCoin, setSelectedCoin] = useState('USDT');
  const [rawTrades, setRawTrades] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [tradeToCancel, setTradeToCancel] = useState(null);
  const autoSettlingRef = useRef(new Set());
  const rawTradesRef = useRef([]);
  const [now, setNow] = useState(Date.now());

  const coinSelectorOptions = useMemo(
    () =>
      SUPPORTED_COINS.map((coin) => ({
        value: coin.value,
        label: coin.label,
        symbol: coin.value,
        price: prices?.[coin.value] ?? null,
        change24h: priceChanges?.[coin.value] ?? null,
        icon: null
      })),
    [priceChanges, prices]
  );

  // Derive live statuses from raw Firestore data + current time tick
  // Filter out terminal/invisible states so they never reach heroCards or My Allocations
  const activeTrades = useMemo(
    () => rawTrades
      .map((t) => ({ ...t, status: getTradeStatus(t, now) }))
      .filter((t) => t.status !== 'cancelled' && t.status !== 'rejected' && t.status !== 'claimed'),
    [rawTrades, now]
  );

  const currentBalance = useMemo(() => {
    return getBalance(selectedCoin, 'arbitrage');
  }, [selectedCoin, getBalance]);

  const availableBalance = useMemo(() => {
    return Math.max(0, Number(currentBalance || 0));
  }, [currentBalance]);

  const reservedBalance = useMemo(() => {
    return getBalance(selectedCoin, 'arbitrageReserved');
  }, [selectedCoin, getBalance]);

  const formattedBalance = useMemo(() => {
    const balance = formatCoinWithUsd(availableBalance, selectedCoin, prices);
    return balance.secondary ? `${balance.primary}  ${balance.secondary}` : balance.primary;
  }, [availableBalance, selectedCoin, prices]);

  const claimProfits = useCallback(
    async (trade) => {
      if (!trade || !currentUser) return;
      setIsSubmitting(true);
      try {
        const tradeRef = doc(db, 'arbitrageTrades', trade.id);
        const profileRef = doc(db, 'userProfiles', currentUser.uid);
        const plan = ARBITRAGE_PLANS.find((p) => p.id === trade.plan);
        const range = computeProfitRange(plan, trade.amount);
        const avgProfit = range.avg || 0;
        const totalPayout = Number(trade.amount || 0) + avgProfit;
        const coin = (trade.coin || '').toUpperCase();

        await runTransaction(db, async (tx) => {
          const tradeSnap = await tx.get(tradeRef);
          if (!tradeSnap.exists()) throw new Error('Trade not found.');
          const storedStatus = tradeSnap.data()?.status;
          if (storedStatus === 'claiming' || storedStatus === 'claimed') {
            throw new Error('This trade has already been claimed.');
          }

          const profileSnap = await tx.get(profileRef);
          if (!profileSnap.exists()) throw new Error('User profile not found.');
          const profileData = profileSnap.data();
          const curAvailable = Number(profileData.accountBalances?.arbitrage?.[coin] || 0);
          const curReserved = Number(profileData.accountBalances?.arbitrageReserved?.[coin] || 0);
          const principal = Number(trade.amount || 0);

          const existingTxns = Array.isArray(profileData.transactions) ? profileData.transactions : [];
          const payoutRecord = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
            type: 'arbitrage_payout',
            status: 'completed',
            coin,
            amount: totalPayout,
            accountType: 'arbitrage',
            direction: 'incoming',
            note: `Arbitrage payout — ${plan?.name || trade.plan} (principal ${formatCoinAmount(trade.amount, trade.coin)} + profit ${formatCoinAmount(avgProfit, trade.coin)})`,
            timestamp: Timestamp.fromDate(new Date()),
            processed: true,
          };
          const updatedTxns = [payoutRecord, ...existingTxns].slice(0, 100);

          tx.update(profileRef, {
            [`accountBalances.arbitrage.${coin}`]: Number((curAvailable + totalPayout).toFixed(8)),
            [`accountBalances.arbitrageReserved.${coin}`]: Number(Math.max(0, curReserved - principal).toFixed(8)),
            transactions: updatedTxns,
            updatedAt: serverTimestamp(),
          });
          tx.delete(tradeRef);
        });

        setAlert({
          type: 'success',
          message: `Successfully claimed ${formatCoinAmount(totalPayout, trade.coin)} (${formatCoinAmount(trade.amount, trade.coin)} principal + ${formatCoinAmount(avgProfit, trade.coin)} profit)`
        });
      } catch (error) {
        console.error('Failed to claim profits', error);
        setAlert({ type: 'error', message: error.message || 'Failed to claim profits. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentUser]
  );

  // Auto-settle removed: it raced with manual claimProfits causing double payouts.
  // Users must click "Claim Profits" manually when a trade completes.
  
  useEffect(() => { rawTradesRef.current = rawTrades; }, [rawTrades]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Polling fallback: re-fetch every 8 s while pending trades exist
  // (guards against onSnapshot push being missed on backgrounded tabs)
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(async () => {
      const hasPending = rawTradesRef.current.some(
        t => (t.status || '').toLowerCase() === 'pending'
      );
      if (!hasPending) return;
      try {
        const snap = await getDocs(
          query(collection(db, 'arbitrageTrades'), where('userId', '==', currentUser.uid))
        );
        const trades = snap.docs.map(d => {
          const data = d.data();
          const startTime = data.startTime?.seconds
            ? data.startTime.seconds * 1000
            : typeof data.startTime === 'number' ? data.startTime : null;
          return { id: d.id, ...data, startTime };
        });
        setRawTrades(trades);
      } catch (e) {
        console.error('Pending-trade poll error:', e);
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    activeTrades
      .filter(t => t.status === 'completed')
      .forEach(t => {
        if (autoSettlingRef.current.has(t.id)) return;
        autoSettlingRef.current.add(t.id);
        claimProfits(t).catch(err => {
          console.error('Auto-settle failed for trade', t.id, err);
          autoSettlingRef.current.delete(t.id);
        });
      });
  }, [activeTrades, currentUser, claimProfits]);

  useEffect(() => {
    if (!alert) return;
    const timer = setTimeout(() => setAlert(null), 6000);
    return () => clearTimeout(timer);
  }, [alert]);

  useEffect(() => {
    if (!currentUser) {
      setRawTrades([]);
      setIsInitialized(true);
      return () => {};
    }

    const tradesQuery = query(
      collection(db, 'arbitrageTrades'),
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(
      tradesQuery,
      (snapshot) => {
        const currentNow = Date.now();
        const trades = snapshot.docs.map((snapshotDoc) => {
          const data = snapshotDoc.data();
          const startTimeMs = data.startTime?.toDate
            ? data.startTime.toDate().getTime()
            : (typeof data.startTime === 'number' ? data.startTime : null);
          const plan = ARBITRAGE_PLANS.find((p) => p.id === data.plan);
          const durationMs = data.durationMs || (plan?.durationDays || 0) * MS_PER_DAY;
          return { id: snapshotDoc.id, ...data, startTime: startTimeMs, durationMs };
        });
        setRawTrades(trades.sort((a, b) => a.startTime - b.startTime));
        setIsInitialized(true);
      },
      (error) => {
        console.error('Failed to listen for arbitrage trades', error);
        setAlert({ type: 'error', message: 'Failed to load arbitrage trades' });
        setIsInitialized(true);
      }
    );

    return () => unsubscribe();
    // NOTE: 'now' is intentionally excluded from deps — we must NOT re-subscribe every second.
    // Status is recomputed from the 'now' ticker state separately.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Admin → User sync: react to admin status changes made via Firebase Console.
  // The admin only needs to flip `arbitrageTrades/{id}.status` in Firestore;
  // this effect handles the rest:
  //   • status='active'   → auto-set `startTime` if missing, mirror to userProfiles.transactions[txRecordId].status
  //   • status='rejected' → refund FULL amount back to available balance, mirror tx record
  //   • status='cancelled' (admin-initiated) → same refund flow
  //   • status='claimed'  → mirror tx record only (claim already handled balance)
  // Each side-effect is idempotent (guarded by `adminProcessed` flag on the trade doc).
  // ─────────────────────────────────────────────────────────────────────────────
  const adminSyncProcessingRef = useRef(new Set());
  useEffect(() => {
    if (!currentUser || !rawTrades.length) return;

    rawTrades.forEach((trade) => {
      const stored = String(trade?.status || '').toLowerCase().trim();
      if (!['active', 'rejected', 'cancelled', 'canceled', 'claimed'].includes(stored)) return;
      if (adminSyncProcessingRef.current.has(trade.id)) return;

      // Skip if user-initiated cancel (already handled by confirmCancelTrade)
      if ((stored === 'cancelled' || stored === 'canceled') && trade.cancelledBy === 'user') return;
      // Skip if already synced
      if (trade.adminSyncProcessed === true && trade.startTime) return;

      adminSyncProcessingRef.current.add(trade.id);

      (async () => {
        const tradeRef = doc(db, 'arbitrageTrades', trade.id);
        const profileRef = doc(db, 'userProfiles', currentUser.uid);
        const coin = (trade.coin || 'USDT').toUpperCase();
        const principal = Number(trade.amount || 0);

        try {
          await runTransaction(db, async (tx) => {
            const tradeSnap = await tx.get(tradeRef);
            if (!tradeSnap.exists()) return;
            const tradeData = tradeSnap.data();
            const liveStatus = String(tradeData.status || '').toLowerCase().trim();
            if (!['active', 'rejected', 'cancelled', 'canceled', 'claimed'].includes(liveStatus)) return;

            const profileSnap = await tx.get(profileRef);
            if (!profileSnap.exists()) return;
            const profileData = profileSnap.data();
            const txns = Array.isArray(profileData.transactions) ? profileData.transactions : [];
            const txRecordId = tradeData.txRecordId;

            // ── Branch: ACTIVE ──
            if (liveStatus === 'active') {
              const tradeUpdate = {};
              if (!tradeData.startTime) {
                tradeUpdate.startTime = serverTimestamp();
                tradeUpdate.approvedAt = serverTimestamp();
                tradeUpdate.approvedBy = tradeData.approvedBy || 'admin';
                tradeUpdate.status = 'active'; // normalize casing
              }
              if (!tradeData.adminSyncProcessed) {
                tradeUpdate.adminSyncProcessed = true;
              }

              // Mirror to userProfiles.transactions
              const profileUpdate = { updatedAt: serverTimestamp() };
              if (txRecordId) {
                let changed = false;
                const updatedTxns = txns.map((rec) => {
                  if (rec?.id === txRecordId && (rec.status || '').toLowerCase() !== 'active') {
                    changed = true;
                    return { ...rec, status: 'active', note: rec.note || 'Arbitrage active — earning yield' };
                  }
                  return rec;
                });
                if (changed) profileUpdate.transactions = updatedTxns;
              }

              if (Object.keys(tradeUpdate).length > 0) tx.update(tradeRef, tradeUpdate);
              if (Object.keys(profileUpdate).length > 1) tx.update(profileRef, profileUpdate);
              return;
            }

            // ── Branch: REJECTED or admin CANCELLED → full refund ──
            if (liveStatus === 'rejected' || liveStatus === 'cancelled' || liveStatus === 'canceled') {
              if (tradeData.refundProcessed === true) return;

              const reserved = Number(profileData.accountBalances?.arbitrageReserved?.[coin] || 0);
              const available = Number(profileData.accountBalances?.arbitrage?.[coin] || 0);
              const refundAmount = principal;

              const refundRecord = {
                id: `arb-refund-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                type: 'arbitrage_refund',
                status: 'completed',
                coin,
                amount: refundAmount,
                accountType: 'arbitrage',
                direction: 'incoming',
                note: `Arbitrage ${liveStatus === 'rejected' ? 'rejected' : 'cancelled'} by admin — full refund`,
                timestamp: Timestamp.fromDate(new Date()),
                processed: true,
              };

              const updatedTxns = txns.map((rec) =>
                rec?.id === txRecordId
                  ? { ...rec, status: liveStatus === 'rejected' ? 'rejected' : 'cancelled' }
                  : rec
              );

              tx.update(profileRef, {
                [`accountBalances.arbitrageReserved.${coin}`]: Number(Math.max(0, reserved - refundAmount).toFixed(8)),
                [`accountBalances.arbitrage.${coin}`]: Number((available + refundAmount).toFixed(8)),
                transactions: [refundRecord, ...updatedTxns].slice(0, 100),
                updatedAt: serverTimestamp(),
              });
              tx.update(tradeRef, {
                refundProcessed: true,
                refundedAt: serverTimestamp(),
                adminSyncProcessed: true,
                status: liveStatus === 'rejected' ? 'rejected' : 'cancelled',
                cancelledBy: liveStatus === 'cancelled' || liveStatus === 'canceled' ? 'admin' : tradeData.cancelledBy || null,
              });
              return;
            }

            // ── Branch: CLAIMED → mirror tx status only (balance handled elsewhere) ──
            if (liveStatus === 'claimed') {
              if (!txRecordId) return;
              let changed = false;
              const updatedTxns = txns.map((rec) => {
                if (rec?.id === txRecordId && (rec.status || '').toLowerCase() !== 'claimed') {
                  changed = true;
                  return { ...rec, status: 'claimed' };
                }
                return rec;
              });
              if (changed) {
                tx.update(profileRef, { transactions: updatedTxns, updatedAt: serverTimestamp() });
              }
              if (!tradeData.adminSyncProcessed) {
                tx.update(tradeRef, { adminSyncProcessed: true });
              }
              return;
            }
          });
        } catch (err) {
          console.error(`[arbitrage admin-sync] failed for trade ${trade.id}:`, err);
        } finally {
          // Allow retry on next snapshot if something changes
          adminSyncProcessingRef.current.delete(trade.id);
        }
      })();
    });
  }, [rawTrades, currentUser]);

  const startTrading = async () => {
    if (!currentUser || !selectedPlan) return;

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setAlert({ type: 'error', message: 'Please enter a valid amount' });
      return;
    }

    if (amountNum > availableBalance) {
      setAlert({ type: 'error', message: `Insufficient ${selectedCoin} balance. Available: ${formattedBalance}` });
      return;
    }

    // Convert coin amount to USDT equivalent for plan range validation
    const rawCoinPrice = prices[selectedCoin];
    const coinPrice = rawCoinPrice ? Number(rawCoinPrice) : 0;
    // For stablecoins treat 1:1 with USD; for others require a live price
    const isStablecoin = selectedCoin === 'USDT' || selectedCoin === 'USDC';
    const effectivePrice = isStablecoin ? 1 : coinPrice;
    if (!isStablecoin && effectivePrice === 0) {
      setAlert({ type: 'error', message: 'Unable to fetch price. Please try again.' });
      return;
    }

    const usdtEquivalent = amountNum * effectivePrice;
    if (usdtEquivalent < selectedPlan.minAmount || usdtEquivalent > selectedPlan.maxAmount) {
      setAlert({ type: 'error', message: `Investment must be between ${formatCurrency(selectedPlan.minAmount)} and ${formatCurrency(selectedPlan.maxAmount)} USDT equivalent` });
      return;
    }

    const defaultLimit = PLAN_SUBSCRIPTION_LIMITS[selectedPlan.id];
    const effectiveLimit = userData?.planLimitOverrides?.[selectedPlan.id] ?? defaultLimit;
    if (effectiveLimit !== undefined) {
      const activeSubs = rawTrades.filter(
        t => t.plan === selectedPlan.id &&
             !['cancelled', 'rejected', 'claimed'].includes(t.status)
      ).length;
      if (activeSubs >= effectiveLimit) {
        setAlert({
          type: 'error',
          message: `You have reached the maximum of ${effectiveLimit} active subscription${effectiveLimit !== 1 ? 's' : ''} for ${selectedPlan.name}. Please contact customer service.`
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const txRecordId = `arb-req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const profileRef = doc(db, 'userProfiles', currentUser.uid);
      const coin = selectedCoin.toUpperCase();

      // Reserve funds immediately by moving from available to reserved
      await runTransaction(db, async (tx) => {
        const profileSnap = await tx.get(profileRef);
        if (!profileSnap.exists()) throw new Error('User profile not found.');
        const profileData = profileSnap.data();
        
        // Check balance again inside transaction
        const curAvailable = Number(profileData.accountBalances?.arbitrage?.[coin] || 0);
        if (curAvailable < amountNum) {
          throw new Error('Insufficient balance');
        }
        
        const curReserved = Number(profileData.accountBalances?.arbitrageReserved?.[coin] || 0);
        const existingTxns = Array.isArray(profileData.transactions) ? profileData.transactions : [];
        
        const requestRecord = {
          id: txRecordId,
          type: 'arbitrage_request',
          status: 'pending',
          coin: selectedCoin,
          amount: amountNum,
          accountType: 'arbitrage',
          direction: 'outgoing',
          note: `Arbitrage request — ${selectedPlan.name} (awaiting on-chain confirmation)`,
          timestamp: Timestamp.fromDate(new Date()),
          processed: false,
        };
        
        tx.update(profileRef, {
          [`accountBalances.arbitrage.${coin}`]: Number((curAvailable - amountNum).toFixed(8)),
          [`accountBalances.arbitrageReserved.${coin}`]: Number((curReserved + amountNum).toFixed(8)),
          transactions: [requestRecord, ...existingTxns].slice(0, 100),
          updatedAt: serverTimestamp(),
        });
      });

      // Create arbitrage trade document with pending status
      const tradeData = {
        plan: selectedPlan.id,
        amount: amountNum,
        coin: selectedCoin,
        requestedAt: serverTimestamp(),
        status: 'pending',
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        durationMs: selectedPlan.durationDays * MS_PER_DAY,
        durationDays: selectedPlan.durationDays,
        txRecordId,
        startTime: null,
        approvedAt: null,
        approvedBy: null,
      };

      await addDoc(collection(db, 'arbitrageTrades'), tradeData);

      setShowPlanModal(false);
      setSelectedPlan(null);
      setAmount('');
      setAlert({
        type: 'success',
        message: `Allocation submitted. ${formatCoinAmount(amountNum, selectedCoin)} is secured and being processed. Your plan will activate shortly.`
      });
    } catch (error) {
      console.error('Error starting trade:', error);
      setAlert({ type: 'error', message: error.message || 'Failed to submit request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmCancelTrade = async () => {
    if (!tradeToCancel || !currentUser) return;

    if (tradeToCancel.status === 'active') {
      setAlert({ type: 'error', message: 'Active positions cannot be withdrawn. Funds are locked until maturity.' });
      setTradeToCancel(null);
      return;
    }
    if (tradeToCancel.status !== 'pending') {
      setAlert({ type: 'error', message: 'Only pending requests can be cancelled.' });
      setTradeToCancel(null);
      return;
    }

    setIsSubmitting(true);
    try {
      const tradeRef = doc(db, 'arbitrageTrades', tradeToCancel.id);
      const profileRef = doc(db, 'userProfiles', currentUser.uid);
      const coin = (tradeToCancel.coin || 'USDT').toUpperCase();

      // Live Firestore pre-check — catches stale UI where admin already approved
      const freshSnap = await getDoc(tradeRef);
      if (!freshSnap.exists()) {
        setAlert({ type: 'error', message: 'This request no longer exists.' });
        setTradeToCancel(null);
        return;
      }
      const freshStatus = freshSnap.data()?.status;
      if (freshStatus === 'active') {
        setAlert({
          type: 'error',
          message: 'This allocation has already been activated. Funds are locked until the plan matures.'
        });
        setTradeToCancel(null);
        return;
      }
      if (freshStatus !== 'pending') {
        setAlert({ type: 'error', message: 'This request can no longer be cancelled.' });
        setTradeToCancel(null);
        return;
      }

      // Calculate penalty
      const plan = ARBITRAGE_PLANS.find(p => p.id === tradeToCancel.plan);
      const penaltyRate = plan?.cancelPenaltyRate ?? 0.05;
      const principal = Number(tradeToCancel.amount);
      const penaltyAmount = Number((principal * penaltyRate).toFixed(8));
      const refundAmount = Number((principal - penaltyAmount).toFixed(8));

      let alreadyProcessed = false;
      await runTransaction(db, async (tx) => {
        const tradeSnap = await tx.get(tradeRef);
        if (!tradeSnap.exists()) { alreadyProcessed = true; return; }
        if (tradeSnap.data()?.status !== 'pending') { alreadyProcessed = true; return; }

        const profileSnap = await tx.get(profileRef);
        if (!profileSnap.exists()) throw new Error('User profile not found.');
        const profileData = profileSnap.data();
        const txRecordId = tradeSnap.data()?.txRecordId;
        const existingTxns = Array.isArray(profileData.transactions) ? profileData.transactions : [];

        const curAvailable = Number(profileData.accountBalances?.arbitrage?.[coin] || 0);
        const curReserved = Number(profileData.accountBalances?.arbitrageReserved?.[coin] || 0);

        // Mark original request record as cancelled with penalty note
        const updatedTxns = txRecordId
          ? existingTxns.map(t =>
              t.id === txRecordId
                ? { ...t, status: 'cancelled', note: `${t.note} (cancelled — ${(penaltyRate * 100).toFixed(0)}% penalty applied)` }
                : t
            )
          : existingTxns;

        // Add penalty deduction record
        const penaltyRecord = {
          id: `arb-cancel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type: 'arbitrage_cancel_penalty',
          status: 'completed',
          coin,
          amount: penaltyAmount,
          accountType: 'arbitrage',
          direction: 'outgoing',
          note: `Early cancellation fee — ${plan?.name || tradeToCancel.plan} (${(penaltyRate * 100).toFixed(0)}% of ${formatCoinAmount(principal, coin)})`,
          timestamp: Timestamp.fromDate(new Date()),
          processed: true,
        };

        tx.update(profileRef, {
          [`accountBalances.arbitrage.${coin}`]: Number((curAvailable + refundAmount).toFixed(8)),
          [`accountBalances.arbitrageReserved.${coin}`]: Number(Math.max(0, curReserved - principal).toFixed(8)),
          transactions: [penaltyRecord, ...updatedTxns].slice(0, 100),
          updatedAt: serverTimestamp()
        });
        tx.update(tradeRef, {
          status: 'cancelled',
          cancelledAt: serverTimestamp(),
          cancelledBy: 'user',
          cancelPenaltyRate: penaltyRate,
          cancelPenaltyAmount: penaltyAmount,
          cancelRefundAmount: refundAmount,
        });
      });

      if (alreadyProcessed) {
        setAlert({ type: 'error', message: 'This request has already been processed.' });
        setTradeToCancel(null);
        return;
      }

      setTradeToCancel(null);
      setAlert({
        type: 'success',
        message: `Request cancelled. ${formatCoinAmount(refundAmount, tradeToCancel.coin)} returned (${formatCoinAmount(penaltyAmount, tradeToCancel.coin)} early-exit fee deducted).`
      });
    } catch (error) {
      console.error('Error cancelling trade:', error);
      setAlert({ type: 'error', message: 'Failed to cancel request. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const heroCards = useMemo(() => {
    const totalLockedUsd = activeTrades
      .filter(trade => trade.status === 'active')
      .reduce((sum, trade) => sum + (trade.amount * (Number(prices[trade.coin]) || 0)), 0);

    const totalPendingUsd = activeTrades
      .filter(trade => trade.status === 'pending')
      .reduce((sum, trade) => sum + (trade.amount * (Number(prices[trade.coin]) || 0)), 0);

    const reservedInSelectedCoin = Number(reservedBalance || 0);
    const totalBalanceInSelectedCoin = Number(availableBalance || 0) + reservedInSelectedCoin;

    const activeCount = activeTrades.filter(t => t.status === 'active').length;
    const pendingCount = activeTrades.filter(t => t.status === 'pending').length;

    return [
      {
        title: 'Arbitrage Balance',
        value: formatCoinAmount(totalBalanceInSelectedCoin, selectedCoin),
        caption: `Available: ${formatCoinAmount(availableBalance, selectedCoin)} | Reserved: ${formatCoinAmount(reservedInSelectedCoin, selectedCoin)}`
      },
      {
        title: 'Total Reserved Value',
        value: formatCurrency(totalLockedUsd + totalPendingUsd),
        caption: `${activeCount} active · ${pendingCount} processing`
      },
      {
        title: 'Est. Daily Income',
        value: formatPercent(ARBITRAGE_PLANS[0].dailyReturnRange.min) + ' - ' + formatPercent(ARBITRAGE_PLANS[ARBITRAGE_PLANS.length-1].dailyReturnRange.max, 2),
        caption: 'Across all plans'
      }
    ];
  }, [activeTrades, availableBalance, reservedBalance, selectedCoin, prices]);

  const selectedPlanRewardRange = useMemo(() => {
    if (!selectedPlan) return null;
    const amountNum = parseFloat(amount);
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      return null;
    }

    const range = computeProfitRange(selectedPlan, amountNum);
    return formatCoinRangeWithUsd(range.min, range.max, selectedCoin, prices);
  }, [amount, selectedPlan, selectedCoin, prices]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading arbitrage data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy via-navyLight to-slate-950 text-slate-50">
      <div className="mx-auto flex max-w-7xl flex-col gap-10">
        
        {/* Header */}
        <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 px-6 py-10 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-transparent" />
          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-xl space-y-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-blue-200">
                Arbitrage Control Room
              </span>
              <h1 className="text-3xl font-bold lg:text-4xl">Deploy capital across managed cross-exchange strategies</h1>
              <p className="text-sm text-gray-200/80 lg:text-base">
                Lock positions in fully hedged arbitrage pools. We handle routing, hedging, and settlement while you
                track your allocations in real time.
              </p>
            </div>
            <div className="grid w-full max-w-2xl grid-cols-1 gap-4 sm:grid-cols-3">
              {heroCards.map((card) => (
                <div key={card.title} className="rounded-2xl border border-white/10 bg-black/30 px-4 py-4 shadow-inner">
                  <p className="text-xs uppercase tracking-wide text-gray-300/70">{card.title}</p>
                  <p className="mt-2 text-lg font-semibold text-white">{card.value}</p>
                  <p className="mt-1 text-xs text-gray-300/60">{card.caption}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Onboarding Guide — shown only when user has no arbitrage balance yet */}
        {availableBalance === 0 && Number(reservedBalance || 0) === 0 && activeTrades.length === 0 && (
          <section className="rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-900/30 via-indigo-900/20 to-slate-900/40 p-6 shadow-lg">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-300">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="text-base font-bold text-white">How to Start Investing in Arbitrage</h3>
                  <p className="mt-1 text-sm text-slate-400">Your arbitrage account is empty. Follow these 3 steps to get started.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {[
                    {
                      step: '01',
                      title: 'Deposit Funds',
                      desc: 'Go to Profile, open Deposit and add funds to your Funding account.',
                      color: 'border-emerald-500/20 bg-emerald-500/8',
                      iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
                    },
                    {
                      step: '02',
                      title: 'Transfer to Arbitrage',
                      desc: 'Go to Profile, open Transfer and move funds from Funding to Arbitrage account.',
                      color: 'border-blue-500/20 bg-blue-500/8',
                      iconPath: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4'
                    },
                    {
                      step: '03',
                      title: 'Select a Plan',
                      desc: 'Come back here, choose a plan below and click Invest Now.',
                      color: 'border-purple-500/20 bg-purple-500/8',
                      iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
                    }
                  ].map(({ step, title, desc, color, iconPath }) => (
                    <div key={step} className={`rounded-xl border ${color} p-4`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5">
                          <svg className="h-4 w-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
                          </svg>
                        </div>
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Step {step}</span>
                      </div>
                      <p className="text-sm font-semibold text-white">{title}</p>
                      <p className="mt-1 text-xs text-slate-400 leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>
                <a
                  href="/profile"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-white shadow transition hover:bg-indigo-500"
                >
                  Go to Profile and Transfer
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Active Trades */}
        {activeTrades.length > 0 && (
          <section>
            <h2 className="text-2xl font-semibold mb-4">My Allocations</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {activeTrades.map(trade => {
                const plan = ARBITRAGE_PLANS.find(p => p.id === trade.plan);
                const isPending = trade.status === 'pending';
                const isActive = trade.status === 'active';
                const isCompleted = trade.status === 'completed';
                const isClaiming = trade.status === 'claiming';
                const remainingMs = (isActive || isCompleted) && trade.startTime
                  ? Math.max(0, (trade.startTime + trade.durationMs) - now)
                  : 0;
                const progress = (isActive || isCompleted) && trade.durationMs > 0
                  ? Math.min(100, ((trade.durationMs - remainingMs) / trade.durationMs) * 100)
                  : 0;
                const statusLabel = isPending ? 'Processing'
                  : isActive ? 'Active'
                  : isCompleted ? 'Matured'
                  : isClaiming ? 'Settling…'
                  : trade.status;
                const statusClass = isPending ? 'bg-amber-500/20 text-amber-300'
                  : isActive ? 'bg-blue-500/20 text-blue-300'
                  : isCompleted ? 'bg-green-500/20 text-green-300'
                  : isClaiming ? 'bg-purple-500/20 text-purple-300'
                  : 'bg-gray-500/20 text-gray-300';

                return (
                  <div key={trade.id} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-lg font-semibold">{plan?.name}</p>
                        <p className="text-sm text-gray-300">{formatCoinAmount(trade.amount, trade.coin)}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${statusClass}`}>{statusLabel}</span>
                    </div>

                    {isPending && (() => {
                      const submittedAt = trade.requestedAt?.seconds
                        ? new Date(trade.requestedAt.seconds * 1000).toLocaleString()
                        : null;
                      const planObj = ARBITRAGE_PLANS.find(p => p.id === trade.plan);
                      const penaltyRate = planObj?.cancelPenaltyRate ?? 0.05;
                      return (
                        <div className="mt-4 rounded-xl bg-blue-500/10 border border-blue-500/20 px-4 py-3">
                          <div className="flex items-start gap-2.5">
                            <svg className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <circle cx="12" cy="12" r="10" />
                              <polyline points="12 6 12 12 16 14" />
                            </svg>
                            <div className="space-y-1">
                              <p className="text-xs font-semibold text-blue-300">Allocation Under Review</p>
                              <p className="text-xs text-blue-200/80">
                                Your {formatCoinAmount(trade.amount, trade.coin)} is secured and being allocated to the {planObj?.name} strategy. Activation typically completes within 24 hours.
                              </p>
                              {submittedAt && (
                                <p className="text-xs text-blue-200/50">Submitted: {submittedAt}</p>
                              )}
                              <p className="text-xs text-blue-300/60 italic">Early withdrawal carries a {(penaltyRate * 100).toFixed(0)}% exit fee on the invested amount.</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {(isActive || isCompleted) && (() => {
                      const startDateStr = trade.startTime ? new Date(trade.startTime).toLocaleString() : null;
                      const endDateStr = (trade.startTime && trade.durationMs)
                        ? new Date(trade.startTime + trade.durationMs).toLocaleString()
                        : null;
                      return (
                        <div className="mt-4 space-y-2">
                          {(startDateStr || endDateStr) && (
                            <div className="rounded-lg bg-white/5 px-3 py-2 space-y-1">
                              {startDateStr && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-400">Start</span>
                                  <span className="text-gray-200">{startDateStr}</span>
                                </div>
                              )}
                              {endDateStr && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-400">End</span>
                                  <span className="text-gray-200">{endDateStr}</span>
                                </div>
                              )}
                            </div>
                          )}
                          <div>
                            <div className="flex justify-between text-xs text-gray-400 mb-1">
                              <span>Time Remaining</span>
                              <span>{isCompleted ? 'Matured' : formatDurationLabel(remainingMs)}</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2.5">
                              <div className="bg-blue-500 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="mt-4 flex gap-4">
                      {isCompleted && (
                        <p className="flex-1 text-center text-xs text-emerald-400 py-2 animate-pulse">
                          Settling automatically…
                        </p>
                      )}
                      {isClaiming && (
                        <p className="flex-1 text-center text-xs text-purple-400 py-2">
                          Settlement in progress, please wait…
                        </p>
                      )}
                      {/* Cancel/withdraw is ONLY available while truly pending and BEFORE admin has touched the trade.
                          Once admin sets status='active' (or any of the activation markers), this disappears for good. */}
                      {isPending
                        && !trade.startTime
                        && !trade.approvedAt
                        && !trade.approvedBy
                        && trade.adminSyncProcessed !== true
                        && (() => {
                        const planObj = ARBITRAGE_PLANS.find(p => p.id === trade.plan);
                        const penaltyRate = planObj?.cancelPenaltyRate ?? 0.05;
                        return (
                          <button
                            onClick={() => setTradeToCancel(trade)}
                            disabled={isSubmitting}
                            className="flex-1 rounded-lg border border-red-500/30 bg-red-500/10 py-2 text-sm font-semibold text-red-300 transition hover:bg-red-500/20 disabled:opacity-50"
                          >
                            Withdraw Request ({(penaltyRate * 100).toFixed(0)}% exit fee)
                          </button>
                        );
                      })()}
                      {isActive && (() => {
                        const endMs = trade.startTime && trade.durationMs ? trade.startTime + trade.durationMs : null;
                        const endDate = endMs ? new Date(endMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
                        return (
                          <div className="flex-1 rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-2.5 text-xs text-blue-300 space-y-0.5">
                            <p className="font-semibold">Strategy Active</p>
                            <p className="text-blue-300/60">
                              {endDate ? `Position matures on ${endDate}` : 'Running until maturity'}
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Plan Selection */}
        <section>
          <h2 className="text-2xl font-semibold mb-4">Available Plans</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {ARBITRAGE_PLANS.map((plan) => (
              <div key={plan.id} className={`relative overflow-hidden rounded-3xl border border-white/10 bg-gray-900/50 shadow-2xl transition-all duration-300 hover:shadow-[0_0_30px_5px_rgba(79,70,229,0.4)] hover:border-indigo-500/50`}>
                <div className="p-6 flex flex-col h-full">
                  <div className="flex-grow">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br ${plan.gradient}`}>
                        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 20V10M18 20V4M6 20V16"/>
                        </svg>
                      </div>
                      <span className="px-3 py-1 text-xs font-semibold text-indigo-300 bg-indigo-500/20 rounded-full border border-indigo-400/30">{plan.riskLevel}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                    <p className="mt-2 text-sm text-gray-400 min-h-[60px]">{plan.description}</p>
                    <div className="mt-6 border-t border-white/10 pt-4 space-y-3 text-sm">
                      <div className="flex justify-between"><span className="text-gray-400">Term</span><span className="font-semibold text-white">{plan.durationLabel}</span></div>
                      <div className="flex justify-between"><span className="text-gray-400">Daily Return</span><span className="font-semibold text-green-400">{formatPercent(plan.dailyReturnRange.min)} - {formatPercent(plan.dailyReturnRange.max)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-400">Investment</span><span className="font-semibold text-white">{formatCurrency(plan.minAmount)} - {formatCurrency(plan.maxAmount)}</span></div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <button
                      onClick={() => {
                        setSelectedPlan(plan);
                        setShowPlanModal(true);
                      }}
                      className="w-full rounded-lg bg-indigo-600 py-3 text-sm font-bold text-white transition-all duration-300 hover:bg-indigo-500 hover:shadow-[0_0_20px_0px_rgba(99,102,241,0.5)]"
                    >
                      Invest Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

      </div>

      {/* Plan Investment Modal */}
      {showPlanModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={() => setShowPlanModal(false)}>
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-gray-900/90 shadow-2xl backdrop-blur-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-xl font-bold">{selectedPlan.name}</h2>
                <button 
                  onClick={() => setShowPlanModal(false)} 
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Close modal"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-400">{selectedPlan.description}</p>
              
              <div className="mt-6">
                <CoinSelectorControl
                  label="Select Coin"
                  value={selectedCoin}
                  onChange={setSelectedCoin}
                  options={coinSelectorOptions}
                />
              </div>

              <div className="mt-4">
                <label className="text-xs text-gray-400">Amount to Invest</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={(() => {
                    const coinPrice = prices[selectedCoin] || 0;
                    if (coinPrice === 0 || selectedCoin === 'USDT' || selectedCoin === 'USDC') {
                      return `Min ${selectedPlan.minAmount}`;
                    }
                    const minCoinAmount = (selectedPlan.minAmount / coinPrice).toFixed(8);
                    return `Min ${minCoinAmount} ${selectedCoin} (~$${selectedPlan.minAmount})`;
                  })()}
                  className="w-full mt-1 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-white"
                />
                <p className="text-xs text-gray-400 mt-1">Balance: {formattedBalance}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Range: {formatCurrency(selectedPlan.minAmount)} - {formatCurrency(selectedPlan.maxAmount)} USDT equivalent
                </p>
              </div>
              
              {parseFloat(amount) > 0 && (() => {
                const amountNum = parseFloat(amount);
                if (isNaN(amountNum) || amountNum <= 0) return null;
                const range = getPlanRange(selectedPlan);
                const profitMin = amountNum * range.minTerm;
                const profitMax = amountNum * range.maxTerm;
                const payoutMin = amountNum + profitMin;
                const payoutMax = amountNum + profitMax;
                return (
                  <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Term</span>
                      <span className="font-medium text-white">{selectedPlan.durationLabel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Daily return</span>
                      <span className="font-medium text-green-400">{formatPercent(range.minDaily)} – {formatPercent(range.maxDaily)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Est. profit at maturity</span>
                      <span className="font-medium text-green-300">+{formatCoinAmount(profitMin, selectedCoin)} – +{formatCoinAmount(profitMax, selectedCoin)}</span>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-2">
                      <span className="font-semibold text-gray-200">Total payout at maturity</span>
                      <span className="font-bold text-white">{formatCoinAmount(payoutMin, selectedCoin)} – {formatCoinAmount(payoutMax, selectedCoin)}</span>
                    </div>
                  </div>
                );
              })()}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowPlanModal(false)}
                  disabled={isSubmitting}
                  className="flex-1 rounded-lg border border-white/10 bg-white/5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={startTrading}
                  disabled={isSubmitting || !amount || parseFloat(amount) <= 0}
                  className="flex-1 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-bold text-white shadow-lg transition hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Processing...' : 'Confirm Investment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {tradeToCancel && (() => {
        const plan = ARBITRAGE_PLANS.find(p => p.id === tradeToCancel.plan);
        const penaltyRate = plan?.cancelPenaltyRate ?? 0.05;
        const principal = Number(tradeToCancel.amount);
        const penaltyAmount = Number((principal * penaltyRate).toFixed(8));
        const refundAmount = Number((principal - penaltyAmount).toFixed(8));
        return (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-gray-900/90 shadow-2xl p-6">
              <h2 className="text-xl font-semibold">Cancel Pending Request</h2>
              <p className="text-sm text-gray-400 mt-1">You are about to cancel your {plan?.name} request.</p>

              {/* Penalty breakdown */}
              <div className="mt-4 rounded-xl border border-red-500/25 bg-red-500/8 p-4 space-y-2">
                <p className="text-xs font-bold uppercase tracking-widest text-red-400">Early Exit Fee Applied</p>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Principal invested</span>
                  <span className="font-semibold text-white">{formatCoinAmount(principal, tradeToCancel.coin)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Cancellation fee ({(penaltyRate * 100).toFixed(0)}%)</span>
                  <span className="font-semibold text-red-400">- {formatCoinAmount(penaltyAmount, tradeToCancel.coin)}</span>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                  <span className="font-bold text-white">Amount returned to you</span>
                  <span className="font-bold text-emerald-300">{formatCoinAmount(refundAmount, tradeToCancel.coin)}</span>
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/8 px-4 py-3">
                <p className="text-xs text-amber-300 leading-relaxed">
                  Once your allocation is confirmed on-chain it becomes active and cannot be cancelled. Your funds are fully secured in the smart contract until the plan matures.
                </p>
              </div>

              <div className="mt-5 flex gap-4">
                <button
                  onClick={() => setTradeToCancel(null)}
                  disabled={isSubmitting}
                  className="flex-1 rounded-lg bg-gray-700 py-2 text-sm font-semibold text-white transition hover:bg-gray-600 disabled:opacity-50"
                >
                  Keep Request
                </button>
                <button
                  onClick={confirmCancelTrade}
                  disabled={isSubmitting}
                  className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-semibold text-white transition hover:bg-red-500 disabled:opacity-50"
                >
                  {isSubmitting ? 'Cancelling...' : 'Confirm Cancel'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Alert */}
      {alert && (
        <div className={`fixed top-20 right-5 z-[100] flex max-w-sm items-start gap-3 rounded-xl px-4 py-3.5 text-white shadow-2xl ${alert.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          <p className="flex-1 text-sm leading-snug">{alert.message}</p>
          <button onClick={() => setAlert(null)} className="mt-0.5 shrink-0 rounded p-0.5 opacity-70 transition hover:opacity-100" aria-label="Dismiss">
            <FiX className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ArbitrageTrading;
